// ============================================================
// CommandCenter/shell/ContextMenu.tsx — the reusable V2 right-click menu.
//
// PHASE_2M P10: one context menu for cards/rows/items across Projects, Tasks,
// Meetings, Portal, Discovery, Calendar, Notifications.
//
// Honesty rules (enforced by shape, not convention):
//   - an action either carries a real onSelect handler, or it carries
//     deferred: 'INTEGRATION_DEFERRED' | 'BACKEND_DEFERRED' — deferred actions
//     render disabled with the deferral label and NEVER fire a fake success.
//   - destructive actions (danger: true) get a built-in confirm step: first
//     activation arms the item ("Confirm <label>"), second activation fires.
//   - Escape / outside click / scroll closes. Arrow keys + Enter navigate.
//
// SSR-safe: no window access at module scope; the menu only portals into the
// DOM after a user gesture (never during renderToString).
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { CC_INK, CC_MONO } from './CommandCenterShell'

export interface ContextMenuAction {
  id: string
  label: string
  icon?: ReactNode
  /** Real handler. Omit ONLY when deferred is set. */
  onSelect?: () => void
  /** Honest deferral — renders disabled with the label; never a fake success. */
  deferred?: 'INTEGRATION_DEFERRED' | 'BACKEND_DEFERRED'
  /** Destructive: requires the built-in two-step confirm. */
  danger?: boolean
  /** Optional fine print under the label (e.g. "writes a receipt"). */
  hint?: string
}

interface MenuState {
  x: number
  y: number
  title?: string
  actions: ContextMenuAction[]
}

/**
 * Hook + element pair. Usage:
 *   const menu = useContextMenu()
 *   <div onContextMenu={(e) => menu.open(e, actions, 'Task — Fix login')} />
 *   {menu.element}
 */
export function useContextMenu() {
  const [state, setState] = useState<MenuState | null>(null)

  const open = useCallback((e: { preventDefault(): void; clientX: number; clientY: number }, actions: ContextMenuAction[], title?: string) => {
    e.preventDefault()
    setState({ x: e.clientX, y: e.clientY, actions, title })
  }, [])

  const close = useCallback(() => setState(null), [])

  const element = state ? <ContextMenuSurface state={state} onClose={close} /> : null
  return { open, close, element, isOpen: state !== null }
}

const itemStyle = (active: boolean, danger?: boolean, disabled?: boolean): CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left',
  padding: '8px 12px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
  background: active ? (danger ? 'rgba(220,38,38,0.12)' : 'rgba(124,58,237,0.12)') : 'transparent',
  border: 'none', color: disabled ? CC_INK.faint : danger ? '#f87171' : CC_INK.primary,
  fontSize: 12, fontWeight: 600, opacity: disabled ? 0.65 : 1,
})

function ContextMenuSurface({ state, onClose }: { state: MenuState; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const [activeIdx, setActiveIdx] = useState(0)
  const [armedId, setArmedId] = useState<string | null>(null)
  const [pos, setPos] = useState({ x: state.x, y: state.y })

  // Keep the menu on-screen.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const x = Math.min(state.x, window.innerWidth - r.width - 8)
    const y = Math.min(state.y, window.innerHeight - r.height - 8)
    setPos({ x: Math.max(8, x), y: Math.max(8, y) })
    el.focus()
  }, [state])

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    const onScroll = () => onClose()
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('scroll', onScroll, true)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('scroll', onScroll, true) }
  }, [onClose])

  const fire = (a: ContextMenuAction) => {
    if (a.deferred || !a.onSelect) return
    if (a.danger && armedId !== a.id) { setArmedId(a.id); return }
    onClose()
    a.onSelect()
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose() }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, state.actions.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fire(state.actions[activeIdx]) }
  }

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={state.title || 'Actions'}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      style={{
        position: 'fixed', left: pos.x, top: pos.y, zIndex: 4000, minWidth: 228, maxWidth: 300,
        padding: 6, borderRadius: 12, outline: 'none',
        background: 'linear-gradient(160deg, #14161f 0%, #0d0f16 100%)',
        border: '1px solid rgba(124,58,237,0.25)',
        boxShadow: '0 18px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,0,0,0.4)',
      }}
    >
      {state.title && (
        <div style={{ padding: '6px 12px 8px', fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', color: CC_INK.faint, fontFamily: CC_MONO, borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {state.title.toUpperCase()}
        </div>
      )}
      {state.actions.map((a, i) => {
        const disabled = !!a.deferred || !a.onSelect
        const armed = armedId === a.id
        return (
          <button
            key={a.id}
            role="menuitem"
            type="button"
            disabled={disabled}
            onMouseEnter={() => setActiveIdx(i)}
            onClick={() => fire(a)}
            style={itemStyle(activeIdx === i, a.danger, disabled)}
            title={a.deferred ? `${a.label} — ${a.deferred}: not wired yet, never faked.` : a.hint}
          >
            {a.icon}
            <span style={{ flex: 1, minWidth: 0 }}>
              {armed ? `Confirm ${a.label.toLowerCase()}?` : a.label}
              {a.hint && !armed && <span style={{ display: 'block', fontSize: 9.5, fontWeight: 500, color: CC_INK.faint, marginTop: 1 }}>{a.hint}</span>}
            </span>
            {a.deferred && (
              <span style={{ fontSize: 8, fontWeight: 800, fontFamily: CC_MONO, letterSpacing: '0.08em', color: '#b8862a', padding: '2px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', flexShrink: 0 }}>
                {a.deferred === 'BACKEND_DEFERRED' ? 'BACKEND' : 'DEFERRED'}
              </span>
            )}
            {armed && (
              <span style={{ fontSize: 8, fontWeight: 800, fontFamily: CC_MONO, color: '#f87171', flexShrink: 0 }}>CLICK AGAIN</span>
            )}
          </button>
        )
      })}
    </div>
  )
}
